import { useEffect, useRef, useState } from 'react';
import { ApiError } from './api';
import { startAtrUpload, type AtrUploadHandle, type AtrUploadProgress } from './atrUpload';
import { fmtBytes } from './atrVideos';
import { fmtRemaining } from './rec/uploader';

/**
 * UNA SUBIDA DE VIDEO A LA VEZ, con su estado para pintar (fase, %, velocidad, error) y las acciones
 * (empezar, reintentar, cancelar). Lo usan la pestaña Uploads (el ATR de la cola) y el modal
 * "Upload videos" de la cita (ATR + informativos), para que el comportamiento sea el mismo:
 * wake lock mientras sube, aviso al cerrar la pestaña, reintento del complete, cancelación con abort.
 */
export type Subida = { apptId: string; kind: 'atr' | 'info'; file: File; p: AtrUploadProgress; handle: AtrUploadHandle; endpoints?: { create: string; base: string } };

export function useAtrUpload(onDone: (apptId: string) => void, t: (k: string, vars?: Record<string, string | number>) => string) {
  const [subida, setSubida] = useState<Subida | null>(null);
  const wakeRef = useRef<{ release: () => Promise<void> } | null>(null);
  const enCurso = subida != null && subida.p.phase !== 'done' && subida.p.phase !== 'failed';
  useEffect(() => {
    if (!enCurso) return undefined;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [enCurso]);

  function soltarPantalla() { void wakeRef.current?.release().catch(() => {}); wakeRef.current = null; }

  function empezar(apptId: string, file: File, opts: { kind?: 'atr' | 'info'; caption?: string | null; endpoints?: { create: string; base: string } } = {}) {
    const kind = opts.kind ?? 'atr';
    // La pantalla no se apaga mientras sube (iOS 16.4+, Android): apagada, iOS congela la subida.
    const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release: () => Promise<void> }> } }).wakeLock;
    void wl?.request('screen').then((w) => { wakeRef.current = w; }).catch(() => {});
    const inicial: AtrUploadProgress = { phase: 'preparing', pct: 0, bytesDone: 0, bytesTotal: file.size, bytesPerSec: 0, remainingSec: null, offline: false, error: null, codec: null };
    const handle = startAtrUpload(file, apptId, (p) => {
      setSubida((s) => (s && s.apptId === apptId && s.kind === kind ? { ...s, p } : s));
      if (p.phase === 'done') { soltarPantalla(); setTimeout(() => { setSubida(null); onDone(apptId); }, 1200); }
      if (p.phase === 'failed') soltarPantalla();
    }, { kind, caption: opts.caption ?? null, endpoints: opts.endpoints });
    setSubida({ apptId, kind, file, p: inicial, handle, endpoints: opts.endpoints });
    handle.promise.catch((e: unknown) => {
      soltarPantalla();
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'cancelled' || msg === 'aborted') { setSubida(null); return; }
      setSubida((s) => (s && s.apptId === apptId ? { ...s, p: { ...s.p, phase: 'failed', error: e instanceof ApiError ? e.message : msg } } : s));
    });
  }
  async function cancelar() {
    if (!subida) return;
    if (subida.p.phase !== 'failed' && !window.confirm(t('insp.up.cancelConfirm'))) return;
    soltarPantalla();
    const s = subida; setSubida(null);
    await s.handle.cancel();
  }
  function reintentar() {
    if (!subida) return;
    if (subida.handle.canRetry()) { setSubida({ ...subida, p: { ...subida.p, phase: 'uploading', error: null } }); subida.handle.retry(); }
    else empezar(subida.apptId, subida.file, { kind: subida.kind, endpoints: subida.endpoints });
  }
  const estado = (p: AtrUploadProgress): string => {
    if (p.phase === 'preparing') return t('insp.up.preparing');
    if (p.phase === 'finishing') return t('insp.up.finishing');
    if (p.phase === 'done') return t('insp.up.done');
    if (p.phase === 'failed') return `${t('insp.up.failed')}${p.error ? ` (${p.error})` : ''}`;
    const partes = [`${p.pct}%`, `${fmtBytes(p.bytesDone)} / ${fmtBytes(p.bytesTotal)}`];
    if (p.remainingSec != null && p.remainingSec > 0) partes.push(t('insp.up.remaining', { time: fmtRemaining(p.remainingSec) }));
    if (p.offline) partes.push(t('insp.up.offline'));
    return partes.join(' · ');
  };
  /** ¿Es un archivo de video? (por tipo o por extensión: el teléfono a veces manda el tipo vacío) */
  const esVideo = (f: File) => /^video\//i.test(f.type) || /\.(mov|mp4|m4v|webm|mkv|3gp|qt)$/i.test(f.name);
  return { subida, enCurso, empezar, cancelar, reintentar, estado, esVideo };
}
